import { useEffect, useCallback, useMemo, useRef } from 'react'
import {
  useStore, useActiveGrid, useItems, useItemsLoading,
  useAgendaView, useValidation, useGridUpdateSeq, useGridsUpdateSeq,
  useLiveItems, useLiveItemsLoading,
} from '../store/index.js'
import { gridsApi, itemsApi, ApiError } from '../lib/api.js'
import { scheduleItemTitle, weekMondayISO } from '../lib/utils.js'
import { addBroadcastDays, broadcastDayKey, broadcastDayWindow, todayBroadcastDayKey } from '../lib/broadcast-day.js'
import { pushGridUndoStep } from '../lib/gridUndo.js'
import { computeNightTail, NIGHT_LOOKAHEAD_HOURS, type NightTail } from '../lib/night-tail.js'
import { getServerNowMs, getServerNow, useServerClock } from './useServerClock.js'
import type { ScheduleItem, ScheduleGrid } from '../types/index.js'

// ─── Hook grilles ─────────────────────────────────────────────────────────────

export function useGridList(channelId: string | null) {
  const { grids, gridsLoading } = useStore()
  const { setGrids, setGridsLoading, setGridsError } = useStore()
  const gridsUpdateSeq = useGridsUpdateSeq()
  const agendaDate = useStore(s => s.agendaView.date)
  const loadVersionRef = useRef(0)

  const load = useCallback(async () => {
    if (!channelId) return
    const loadVersion = ++loadVersionRef.current
    setGridsLoading(true)
    try {
      const res = await gridsApi.list({ channelId, gridType: 'WEEKLY', limit: 50 })
      if (loadVersion !== loadVersionRef.current) return
      const visibleGrids = res.data.filter((grid: ScheduleGrid) => grid.status !== 'ARCHIVED')
      setGrids(visibleGrids)
      // Tout ce qui est visible dans la page Grille est jouable. Les anciens
      // statuts DRAFT/VALIDATED/ACTIVE ne participent donc jamais au choix.
      const targetMonday = weekMondayISO(agendaDate)
      const selected = visibleGrids.find((grid: ScheduleGrid) => grid.id === useStore.getState().activeGridId)
      const selectedMatchesWeek = selected?.weekStart
        ? weekMondayISO(selected.weekStart) === targetMonday
        : false
      if (!selectedMatchesWeek) {
        const matching = visibleGrids
          .filter((grid: ScheduleGrid) => grid.weekStart && weekMondayISO(grid.weekStart) === targetMonday)
          .sort((a: ScheduleGrid, b: ScheduleGrid) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          )[0]
        useStore.getState().setActiveGrid(matching?.id ?? null)
      }
    } catch (err) {
      if (loadVersion !== loadVersionRef.current) return
      setGridsError(err instanceof ApiError ? err.message : 'Erreur')
    } finally {
      if (loadVersion === loadVersionRef.current) setGridsLoading(false)
    }
  }, [channelId, gridsUpdateSeq, agendaDate, setGrids, setGridsLoading, setGridsError])

  useEffect(() => {
    void load()
    return () => { loadVersionRef.current++ }
  }, [load])

  return { grids, loading: gridsLoading, reload: load }
}

// ─── Hook items (fenêtre temporelle) ─────────────────────────────────────────

/**
 * Charge les items de la grille affichée pour le jour consulté dans l'agenda.
 * Se re-déclenche quand la grille sélectionnée ou la date change.
 */
export function useAgendaItems() {
  const items           = useItems()
  const loading         = useItemsLoading()
  const agendaView      = useAgendaView()
  const activeGrid      = useActiveGrid()
  const gridUpdateSeq   = useGridUpdateSeq()
  const { setItems, setItemsLoading, setAgendaNightTail } = useStore()
  const storedTail = useStore(state => state.agendaNightTail)
  const loadVersionRef = useRef(0)
  // Queue de nuit : la journée se prolonge jusqu'au premier élément du
  // lendemain (`lib/night-tail.ts`). Ses passages d'infoservice rejoignent la
  // liste ; l'élément du lendemain, lui, ne sert que de borne.
  // Rangée sous sa clé grille + jour : une queue lue pour une autre journée,
  // ou avant que la grille disparaisse, n'est jamais rendue.
  const tailKey = activeGrid ? `${activeGrid.id}|${agendaView.date}` : null
  const nightTail: Omit<NightTail, 'items'> | null = storedTail && storedTail.key === tailKey ? storedTail : null

  const load = useCallback(async () => {
    const loadVersion = ++loadVersionRef.current
    if (!activeGrid) {
      setItems([])
      setItemsLoading(false)
      return
    }
    setItemsLoading(true)
    try {
      // Fenêtre = journée d'antenne affichée, de minuit à minuit exclu. La
      // borne « 23:59:59.999 » d'avant laissait la dernière milliseconde de la
      // nuit hors de toute journée, et les bornes se calculaient en heure du
      // poste et non de la chaîne (`lib/broadcast-day.ts`).
      const { from, to } = broadcastDayWindow(agendaView.date)
      const lookaheadEnd = new Date(to.getTime() + NIGHT_LOOKAHEAD_HOURS * 3_600_000)

      // Une seule lecture, nuit comprise : `getItems` lit déjà toute l'antenne
      // du canal, y compris la grille de la semaine suivante le dimanche soir.
      const loaded = await gridsApi.getItems(activeGrid.id, from.toISOString(), lookaheadEnd.toISOString())
      if (loadVersion !== loadVersionRef.current) return
      const dayEndMs = to.getTime()
      const dayItems = loaded.filter(item => new Date(item.startTime).getTime() < dayEndMs)
      const tail = computeNightTail(dayItems, loaded.filter(item => new Date(item.startTime).getTime() >= dayEndMs), dayEndMs)
      setItems([...dayItems, ...tail.items])
      setAgendaNightTail({ key: `${activeGrid.id}|${agendaView.date}`, endMs: tail.endMs, nextItem: tail.nextItem })
    } catch {
      if (loadVersion !== loadVersionRef.current) return
      // La queue lue avant reste rangée sous sa clé ; sans éléments, elle ne
      // borne plus qu'une grille vide.
      setItems([])
    } finally {
      if (loadVersion === loadVersionRef.current) setItemsLoading(false)
    }
  }, [activeGrid?.id, agendaView.date, gridUpdateSeq, setAgendaNightTail, setItems, setItemsLoading])

  useEffect(() => {
    void load()
    return () => { loadVersionRef.current++ }
  }, [load])

  return { items, loading, reload: load, nightTail }
}

// ─── Hook items du jour d'antenne réel ────────────────────────────────────────

/**
 * Charge les items du jour d'antenne et du lendemain, indépendamment de la date ou de
 * la semaine consultées dans la Grille — c'est cette liste qui alimente le
 * bandeau permanent (`OnAirBanner`) et le rail de la Grille. Sans elle, le
 * bandeau affichait « fin de grille » dès qu'un opérateur naviguait vers un
 * autre jour ou une autre semaine, la banque d'items partagée pointant alors
 * sur la journée consultée plutôt que sur la diffusion réelle.
 *
 * À monter une seule fois, au niveau racine (`AppLayout`).
 */
export function useLiveSchedule(channelId: string | null) {
  const items         = useLiveItems()
  const loading       = useLiveItemsLoading()
  const gridUpdateSeq = useGridUpdateSeq()
  const gridsUpdateSeq = useGridsUpdateSeq()
  const { nowMs }      = useServerClock()
  const { setLiveItems, setLiveItemsLoading } = useStore()
  const loadVersionRef = useRef(0)

  const liveDayKey = useMemo(() => todayBroadcastDayKey(getServerNow()), [nowMs])

  const load = useCallback(async () => {
    const loadVersion = ++loadVersionRef.current
    if (!channelId) {
      setLiveItems([])
      setLiveItemsLoading(false)
      return
    }
    setLiveItemsLoading(true)
    try {
      const targetMonday = weekMondayISO(liveDayKey)
      let res = await gridsApi.list({ channelId, gridType: 'WEEKLY', weekStart: targetMonday, limit: 5 })
      if (loadVersion !== loadVersionRef.current) return
      if (!(res.data as ScheduleGrid[]).some(grid => grid.status !== 'ARCHIVED')) {
        // Lundi, la grille à venir peut ne pas encore exister alors que celle
        // du dimanche porte toujours le programme en cours. Elle suffit comme
        // ancre : getItems recherche les chevauchements sur tout le canal.
        const previousMonday = weekMondayISO(broadcastDayKey(addBroadcastDays(targetMonday, -1)))
        res = await gridsApi.list({ channelId, gridType: 'WEEKLY', weekStart: previousMonday, limit: 5 })
        if (loadVersion !== loadVersionRef.current) return
      }
      const liveGrid = (res.data as ScheduleGrid[])
        .filter(grid => grid.status !== 'ARCHIVED')
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0]
      if (!liveGrid) {
        setLiveItems([])
        return
      }
      // Le suivant peut commencer demain, y compris dans la semaine suivante.
      // L'API lit les chevauchements sur tout le canal, pas seulement liveGrid.
      const { from } = broadcastDayWindow(liveDayKey)
      const to = addBroadcastDays(liveDayKey, 2)
      const items = await gridsApi.getItems(liveGrid.id, from.toISOString(), to.toISOString())
      if (loadVersion !== loadVersionRef.current) return
      setLiveItems(items)
    } catch {
      if (loadVersion !== loadVersionRef.current) return
      setLiveItems([])
    } finally {
      if (loadVersion === loadVersionRef.current) setLiveItemsLoading(false)
    }
  }, [channelId, liveDayKey, gridUpdateSeq, gridsUpdateSeq, setLiveItems, setLiveItemsLoading])

  useEffect(() => {
    void load()
    return () => { loadVersionRef.current++ }
  }, [load])

  return { items, loading, reload: load }
}

// ─── Actions grille ───────────────────────────────────────────────────────────

export function useScheduleActions() {
  const {
    upsertItem, removeItem, setConflicts, clearConflicts,
    setValidationResult, setValidating,
    incrementGridUpdateSeq,
  } = useStore()

  // ── Créer un item ──────────────────────────────────────────────────────────
  const createItem = useCallback(async (data: Parameters<typeof itemsApi.create>[0]) => {
    const result = await itemsApi.create(data)
    const { item, conflicts } = result
    ;(result.items ?? [item]).forEach(upsertItem)
    incrementGridUpdateSeq() // Force le rafraîchissement global
    if (conflicts.length > 0) {
      setConflicts((conflicts as ScheduleItem[]).map(c => c.id))
    }
    result.warnings?.forEach(warning => useStore.getState().pushToast(warning, 'warning', 10000))
    return { item, conflicts }
  }, [upsertItem, setConflicts, incrementGridUpdateSeq])

  // ── Déplacer un item (drag & drop) ─────────────────────────────────────────
  const moveItem = useCallback(async (id: string, startTime: string) => {
    // Mise à jour optimiste immédiate
    const currentItem = useStore.getState().items.find(i => i.id === id)
    if (currentItem) {
      const newEnd = new Date(
        new Date(startTime).getTime() + currentItem.durationSec * 1000
      ).toISOString()
      upsertItem({ ...currentItem, startTime, endTime: newEnd })
    }

    try {
      const result = await itemsApi.move(id, startTime)
      const { item, conflicts } = result
      ;(result.items ?? []).forEach(upsertItem)
      upsertItem(item)  // Sync avec la réponse serveur
      setConflicts((conflicts as ScheduleItem[]).map(c => c.id))
      // Annulation : l'inverse d'un déplacement est exact et connu ici, inutile
      // de relire la semaine deux fois comme le font les actions à photo. Seul
      // l'élément saisi est enregistré : sur un bloc multipartie, le serveur
      // refuse qu'on déplace une partie seule et ramène le bloc entier avec sa
      // racine.
      if (currentItem) {
        pushGridUndoStep(
          `déplacement de ${scheduleItemTitle(currentItem) ?? 'un élément'}`,
          broadcastDayKey(currentItem.startTime),
          { created: [], removed: [], changed: [{ before: currentItem, after: item }] },
        )
      }
      return { item, conflicts }
    } catch (err) {
      // Rollback optimiste en cas d'erreur
      if (currentItem) upsertItem(currentItem)
      throw err
    }
  }, [upsertItem, setConflicts])

  // ── Déplacer une sélection d'un seul geste ────────────────────────────────
  const moveGroup = useCallback(async (ids: string[], deltaMs: number) => {
    const before = useStore.getState().items.filter(item => ids.includes(item.id))
    const shift = (iso: string) => new Date(new Date(iso).getTime() + deltaMs).toISOString()
    // Optimiste, comme le déplacement simple ; le serveur tranche et renvoie
    // les éléments réellement déplacés.
    before
      .filter(item => !['LOADING', 'ON_AIR', 'DONE'].includes(item.status))
      .forEach(item => upsertItem({ ...item, startTime: shift(item.startTime), endTime: shift(item.endTime) }))

    try {
      const result = await itemsApi.moveGroup(ids, deltaMs)
      result.items.forEach(upsertItem)
      setConflicts(result.conflicts.map(conflict => conflict.id))
      const byId = new Map(result.items.map(item => [item.id, item]))
      const changed = before
        .filter(item => byId.has(item.id))
        .map(item => ({ before: item, after: byId.get(item.id)! }))
      if (changed.length > 0) {
        pushGridUndoStep(
          `déplacement de ${changed.length} éléments`,
          broadcastDayKey(changed[0].before.startTime),
          { created: [], removed: [], changed },
        )
      }
      return result
    } catch (err) {
      before.forEach(upsertItem)
      throw err
    }
  }, [upsertItem, setConflicts])

  // ── Redimensionner la plage logique d'infoservice ────────────────────────
  const resizeInfoservice = useCallback(async (id: string, startTime: string, endTime: string, memberIds: string[]) => {
    const result = await itemsApi.resizeInfoservice(id, startTime, endTime, memberIds)
    result.removedIds.forEach(removeItem)
    ;(result.items ?? [result.item]).forEach(upsertItem)
    setConflicts((result.conflicts as ScheduleItem[]).map(conflict => conflict.id))
    incrementGridUpdateSeq()
    return result
  }, [incrementGridUpdateSeq, removeItem, setConflicts, upsertItem])

  // ── Tirer la fin d'un direct ───────────────────────────────────────────────
  // Le serveur rogne ou rallonge l'infoservice qui suit : la grille est relue.
  const resizeLive = useCallback(async (id: string, endTime: string) => {
    const result = await itemsApi.resizeLive(id, endTime)
    incrementGridUpdateSeq()
    return result
  }, [incrementGridUpdateSeq])

  // ── Supprimer un item ──────────────────────────────────────────────────────
  const deleteItem = useCallback(async (id: string) => {
    // Optimiste : retire immédiatement de l'UI
    const allItems = useStore.getState().items
    const selected = allItems.find(i => i.id === id)
    const backup = selected ? [selected] : []
    backup.forEach(item => removeItem(item.id))
    try {
      await itemsApi.delete(id)
    } catch (err) {
      // Rollback
      backup.forEach(upsertItem)
      throw err
    }
  }, [removeItem, upsertItem])

  // ── Valider la grille ──────────────────────────────────────────────────────
  const validateGrid = useCallback(async (gridId: string, autoFix = false) => {
    setValidating(true)
    try {
      const result = await gridsApi.validate(gridId, autoFix)
      setValidationResult(result)

      const { pushToast } = useStore.getState()
      const errors   = result.issues.filter((i: any) => i.severity === 'ERROR')
      const warnings = result.issues.filter((i: any) => i.severity === 'WARNING')

      // Une seule carte de synthèse dans la pile ; chaque problème est rangé,
      // silencieusement, dans l'historique de la cloche, où il se lit en entier.
      // L'ancienne version empilait jusqu'à sept cartes d'un coup.
      const archive = (issue: any, severity: 'error' | 'warning') => {
        const item = useStore.getState().items.find(candidate => candidate.id === issue.itemId)
        pushToast(issue.message, severity, undefined, undefined, {
          silent: true,
          focus: item ? { itemId: item.id, iso: item.startTime, gridId: item.gridId } : undefined,
        })
      }
      warnings.forEach((w: any) => archive(w, 'warning'))
      errors.forEach((e: any) => archive(e, 'error'))

      if (result.valid) {
        pushToast(
          warnings.length > 0
            ? `Contrôle terminé · ${result.coveragePercent}% · ${warnings.length} avertissement(s), détail dans l’historique`
            : `Contrôle terminé · ${result.coveragePercent}% de couverture`,
          warnings.length > 0 ? 'warning' : 'success',
        )
      } else {
        pushToast(
          `${errors.length} erreur(s) détectée(s) dans la grille${warnings.length > 0 ? ` · ${warnings.length} avertissement(s)` : ''} — détail dans l’historique`,
          'error',
          undefined,
          { label: 'Détail', onClick: () => useStore.getState().setNoticeCenterOpen(true) },
        )
      }

      return result
    } finally {
      setValidating(false)
    }
  }, [setValidating, setValidationResult])

  // ── Exporter la playlist ───────────────────────────────────────────────────
  const exportPlaylist = useCallback(async (gridId: string) => {
    return gridsApi.export(gridId)
  }, [])

  return {
    createItem,
    moveItem,
    moveGroup,
    resizeInfoservice,
    resizeLive,
    deleteItem,
    validateGrid,
    exportPlaylist,
    clearConflicts: () => useStore.getState().clearConflicts(),
  }
}

// ─── Hook progression de l'item en cours ─────────────────────────────────────

/**
 * Calcule la progression précise de l'item ON_AIR basée sur l'heure réelle.
 * Retourne { elapsedSec, remainingSec, progressPct, durationSec }.
 */
export function useOnAirProgress() {
  const items       = useItems()
  const onAirItem   = items.find(i => i.status === 'ON_AIR')

  if (!onAirItem) return null

  // Progression mesuree sur l'horloge du serveur : c'est elle qui commande le
  // depart des elements, un poste desynchronise afficherait une barre en avance.
  const now         = getServerNowMs()
  const startMs     = new Date(onAirItem.startTime).getTime()
  const endMs       = new Date(onAirItem.endTime).getTime()
  const elapsedSec  = Math.max(0, Math.round((now - startMs) / 1000))
  const remainingSec = Math.max(0, Math.round((endMs - now) / 1000))
  const progressPct  = Math.min(100, Math.round((elapsedSec / onAirItem.durationSec) * 100))

  return {
    item:        onAirItem,
    elapsedSec,
    remainingSec,
    progressPct,
    durationSec: onAirItem.durationSec,
  }
}
